// Client-side automatic checks run on a selected video before/around upload.
// NOTE: "codec" here is container/MIME level only — the browser can't reliably
// report the true codec. Loading the file into a <video> element does give a
// real decodability signal (unsupported/corrupt files fail), which is the
// closest client-side proxy. For authoritative codec checks, run ffprobe
// server-side.
import SparkMD5 from 'spark-md5'

const MB = 1024 * 1024

// Read duration + dimensions by loading the file into a <video>. Rejects when
// the browser can't decode it (a rough codec/format sanity check).
export function readVideoMetadata(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const video = document.createElement('video')
    video.preload = 'metadata'
    const done = (fn) => { URL.revokeObjectURL(url); fn() }
    video.onloadedmetadata = () =>
      done(() => resolve({ duration: video.duration, width: video.videoWidth, height: video.videoHeight }))
    video.onerror = () => done(() => reject(new Error('unreadable')))
    video.src = url
  })
}

// Incremental MD5 so large videos don't exhaust memory.
export function computeMD5(file, { chunkSize = 8 * MB, onProgress } = {}) {
  return new Promise((resolve, reject) => {
    const spark = new SparkMD5.ArrayBuffer()
    const reader = new FileReader()
    let offset = 0
    const readNext = () => reader.readAsArrayBuffer(file.slice(offset, offset + chunkSize))
    reader.onload = (e) => {
      spark.append(e.target.result)
      offset += e.target.result.byteLength
      if (onProgress) onProgress(Math.min(1, offset / file.size))
      offset < file.size ? readNext() : resolve(spark.end())
    }
    reader.onerror = () => reject(new Error('read error'))
    readNext()
  })
}

const DEFAULTS = {
  maxSizeMB: 1024,
  minDurationSec: 1,
  maxDurationSec: 3600,
  acceptedTypes: ['video/mp4', 'video/quicktime', 'video/webm'],
}

// Run every check and return the collected metadata plus a pass/fail verdict
// with human-readable reasons.
export async function checkVideo(file, options = {}) {
  const opts = { ...DEFAULTS, ...options }
  const failureReasons = []

  const result = {
    filename: file.name,
    contentType: file.type || '',
    byteSize: file.size,
    codec: file.type || (file.name.split('.').pop() || '').toLowerCase(),
    durationSeconds: null,
    md5: null,
  }

  // size
  if (file.size === 0) failureReasons.push('The file is empty.')
  else if (opts.maxSizeMB && file.size > opts.maxSizeMB * MB)
    failureReasons.push(`File is too large (max ${opts.maxSizeMB} MB).`)

  // format / codec (container-level; true codec needs server-side ffprobe)
  if (opts.acceptedTypes?.length && result.contentType && !opts.acceptedTypes.includes(result.contentType))
    failureReasons.push(`Unsupported format "${result.contentType}".`)

  // duration + decodability
  try {
    const meta = await readVideoMetadata(file)
    result.durationSeconds = Number.isFinite(meta.duration) ? meta.duration : null
    if (!meta.width) failureReasons.push('File does not contain a video track.')
    if (result.durationSeconds == null) failureReasons.push('Could not read video duration.')
    else if (opts.minDurationSec && result.durationSeconds < opts.minDurationSec)
      failureReasons.push(`Video is too short (min ${opts.minDurationSec}s).`)
    else if (opts.maxDurationSec && result.durationSeconds > opts.maxDurationSec)
      failureReasons.push(`Video is too long (max ${opts.maxDurationSec}s).`)
  } catch {
    failureReasons.push('The file could not be decoded as a video (unsupported codec or corrupt).')
  }

  // md5sum
  try {
    result.md5 = await computeMD5(file, { onProgress: options.onMd5Progress })
  } catch {
    failureReasons.push('Could not compute the file checksum.')
  }

  return { ...result, passed: failureReasons.length === 0, failureReasons }
}
